'use client';

import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react';
import { useBlobUrl } from '@/lib/useBlobUrl';
import { log } from '@/lib/log';

type BlobImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src: string | undefined | null;
  /**
   * When true, the blob is only resolved (and the underlying ObjectURL
   * created) once the element scrolls into the viewport. Until then,
   * a transparent placeholder is rendered so layout is stable and the
   * IntersectionObserver has a target.
   *
   * Use on long lists where rendering all images at once exhausts iOS
   * Safari's WebContent memory budget and triggers an OS-level kill of
   * the page (Veronika's iPhone, build 1003, /yarn with 34 yarns).
   */
  lazy?: boolean;
};

// 1×1 transparent SVG. Same color profile semantics as an empty img,
// but renders without a console warning and lets the IntersectionObserver
// attach to a real <img> element with the caller's className/style.
const PLACEHOLDER =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"/>';

/**
 * Drop-in replacement for `<img>` that understands the three photo-reference
 * shapes used in the app:
 *
 *   - `undefined` / empty → renders nothing (or, with `lazy`, a placeholder)
 *   - `data:image/…` legacy data URL → rendered as-is
 *   - `http(s)://…` → rendered as-is
 *   - `blob:sha256-<hex>` → resolved asynchronously via the blob store,
 *     invisible until the ObjectURL is ready
 */

// Lazy mode is a workaround for iOS WebContent killing PWAs that allocate
// dozens of ObjectURLs at once on /yarn (Veronika's iPhone, build 1003).
// Desktop browsers have plenty of memory, so the lazy gate just adds a
// way for IntersectionObserver-on-placeholder to silently fail to fire
// (Mac Safari, 2026-04-29). Detect iOS at module scope and disable lazy
// everywhere else — same UX outcome, no risk of stuck placeholders.
const IS_IOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

export default function BlobImage({ src, alt = '', lazy = false, ...rest }: BlobImageProps) {
  const effectiveLazy = lazy && IS_IOS;
  const ref = useRef<HTMLImageElement | null>(null);
  const [intersected, setIntersected] = useState(!effectiveLazy);

  useEffect(() => {
    if (!effectiveLazy || intersected) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIntersected(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIntersected(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    // Safety net: if the intersection callback hasn't fired within 2s,
    // resolve anyway. Mac Safari sometimes never reports intersection
    // for SVG-data-URL-placeholder <img>s even though they're in viewport.
    const fallback = window.setTimeout(() => setIntersected(true), 2000);
    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [effectiveLazy, intersected]);

  const resolved = useBlobUrl(intersected ? src : undefined);

  // If the resolved image fails to decode (a non-empty but corrupt/partial
  // blob — zero-byte ghosts are already filtered out by getBlobUrl), fall
  // back to the same "render nothing" state as an unresolved image so the
  // caller's blank/colour tile shows instead of the browser's broken-image
  // icon. Reset whenever `src` changes so a new photo gets a fresh attempt.
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [src]);

  // Legacy non-lazy callers still get the "render nothing until resolved"
  // behavior they expect. Lazy callers always render an <img> so the
  // IntersectionObserver has a target with the right size.
  if ((!resolved || errored) && !effectiveLazy) return null;

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={errored ? PLACEHOLDER : (resolved || PLACEHOLDER)}
      alt={alt}
      {...rest}
      onError={() => {
        if (errored) return;
        setErrored(true);
        log.warn('app', 'blob-img-decode-failed', { src });
      }}
    />
  );
}
